/* =========================================================================
   TAVU — shared front-end behaviour
   Vanilla, no dependencies, deferred. Every behaviour is progressive:
   with JS disabled the page is still fully readable and navigable.

   Re-binds on Shopify theme-editor events so sections keep working after
   a merchant edits them without a full page reload.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- scroll reveal --------------------------------------------------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.reveal:not(.in)');
    if (!els.length) return;

    // No IntersectionObserver (or reduced motion) -> show everything immediately.
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- FAQ accordion --------------------------------------------------- */
  // Height is measured per-item so long answers are never clipped.
  function setFaqHeight(item) {
    var answer = item.querySelector('.faq-a');
    if (!answer) return;
    item.style.setProperty('--faq-h', answer.scrollHeight + 'px');
  }

  function initFaq(root) {
    (root || document).querySelectorAll('.faq-item').forEach(function (item) {
      var btn = item.querySelector('.faq-q');
      if (!btn || btn.dataset.tavuBound) return;
      btn.dataset.tavuBound = '1';

      var answer = item.querySelector('.faq-a');
      var open = item.classList.contains('open');

      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (answer) {
        if (!answer.id) answer.id = 'faq-a-' + Math.random().toString(36).slice(2, 9);
        btn.setAttribute('aria-controls', answer.id);
      }
      if (open) setFaqHeight(item);

      btn.addEventListener('click', function () {
        var nowOpen = !item.classList.contains('open');
        // Measure before the class flips so scrollHeight is accurate.
        setFaqHeight(item);
        item.classList.toggle('open', nowOpen);
        btn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      });
    });
  }

  // Answers reflow on resize; refresh the cached heights of open items.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('.faq-item.open').forEach(setFaqHeight);
    }, 150);
  });

  /* ---- mobile nav ------------------------------------------------------ */
  function initMobileNav(root) {
    (root || document).querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      if (btn.dataset.tavuBound) return;
      btn.dataset.tavuBound = '1';

      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var head = btn.closest('.site-head');
        var menu = head && head.querySelector('.mobile-nav');
        if (!menu) return;
        var open = menu.classList.toggle('open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---- rotating announcement ------------------------------------------- */
  function initAnnounce(root) {
    (root || document).querySelectorAll('[data-rotate]').forEach(function (bar) {
      if (bar.dataset.tavuBound) return;
      bar.dataset.tavuBound = '1';

      var target = bar.querySelector('[data-rotate-text]');
      if (!target) return;

      var messages = [];
      try {
        messages = JSON.parse(bar.getAttribute('data-messages') || '[]');
      } catch (e) {
        messages = [];
      }
      // Nothing to rotate through, or the visitor asked for less motion.
      if (messages.length < 2 || reduceMotion) return;

      var index = 0;
      setInterval(function () {
        if (document.hidden) return;
        index = (index + 1) % messages.length;
        target.classList.add('is-fading');
        setTimeout(function () {
          target.textContent = messages[index];
          target.classList.remove('is-fading');
        }, 400);
      }, 5000);
    });
  }

  /* ---- footer newsletter ----------------------------------------------- */
  // Shopify's own customer form posts and redirects; this only guards the
  // empty-submit case so the browser's native validation message shows.
  function initNewsletter(root) {
    (root || document).querySelectorAll('[data-news]').forEach(function (form) {
      if (form.dataset.tavuBound) return;
      form.dataset.tavuBound = '1';
      form.addEventListener('submit', function (e) {
        var input = form.querySelector('input[type="email"]');
        if (input && !input.value.trim()) e.preventDefault();
      });
    });
  }

  /* ---- cart drawer skeleton -------------------------------------------
     Prestige opens the drawer in ~75ms but then leaves it empty for ~700ms
     while Shopify server-renders the bundled cart-drawer section into the
     /cart/add.js response (measured 525-650ms across runs, plus a redundant
     /cart.js round trip after it).

     An empty panel reads as broken, so we show a skeleton for that window.
     This does not make the request faster; it makes the wait legible.

     Scoped to submits of the PDP form specifically, rather than the global
     theme:loading:start, because that event also fires for variant changes
     and predictive search - which must not flash a cart skeleton.
     -------------------------------------------------------------------- */
  function initCartSkeleton(root) {
    var forms = (root || document).querySelectorAll('form[action$="/cart/add"]:not([data-skel])');
    if (!forms.length) return;

    forms.forEach(function (form) {
      form.setAttribute('data-skel', '');
      form.addEventListener('submit', function () {
        var drawer = document.getElementById('cart-drawer');
        if (!drawer) return;                    // cart type is page, nothing to skeleton
        drawer.classList.add('is-loading');
        // Open it NOW. Prestige only opens the drawer once /cart/add.js resolves,
        // which measured 525-650ms, so clicking felt like nothing happened at all.
        if (typeof drawer.show === 'function') {
          try { drawer.show(); } catch (e) { /* older Prestige: let it open itself */ }
        }
        // The drawer's chrome lives in shadow DOM and its only light-DOM children
        // are the header <p slot="header"> and the cart body, so inject a real
        // node rather than trying to style pseudo-elements across the boundary.
        if (!drawer.querySelector('.tavu-skel')) {
          var skel = document.createElement('div');
          skel.className = 'tavu-skel';
          skel.setAttribute('aria-hidden', 'true');
          skel.innerHTML = '<div class="tavu-skel__row"></div><div class="tavu-skel__row"></div>';
          drawer.appendChild(skel);
        }
        window.clearTimeout(form._skelTimer);
        form._skelTimer = window.setTimeout(clearSkeleton, 8000);
      });
    });
  }

  function clearSkeleton() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-loading');
    var skel = drawer.querySelector('.tavu-skel');
    if (skel) skel.remove();
  }

  document.documentElement.addEventListener('theme:loading:end', clearSkeleton);

  function initAll(root) {
    initReveal(root);
    initFaq(root);
    initMobileNav(root);
    initAnnounce(root);
    initNewsletter(root);
    initCartSkeleton(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  window.addEventListener('load', function () { document.body.classList.add('loaded'); });

  // Theme editor: a re-rendered section needs its behaviour re-bound.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
